/** @jsxImportSource @emotion/react */
import * as React from "react";
import LabelGroup from "../../forms/elements/LabelGroup";
import {
  COLUMN_SPACING,
  ROW_SPACING,
  ROW_SPACING_WIDE,
} from "../../forms/styles";
import { getResource } from "../../language/resources/getResource";
import { Dropdown } from "../../forms/elements/select/Dropdown";
import { useDispatch, useSelector } from "react-redux";
import { IAppState } from "../../core/redux/IAppState";
import { isNilOrEmpty } from "../../language/module";
import DateInput from "../../forms/elements/DateInput";
import { TinyText } from "../../styles/styles";
import ErrorView from "../../forms/ErrorView";
import { EntitlementHotlistType as HotlistType } from "../../guest/hotlist/entitlementHotlistType";
import { getHotlistTypesAction } from "../../guest/hotlist/getEntitlementHotlistTypesAction";
import * as Buttons from "../../forms/elements/buttons";
import { validateHotlistReportDates, validateHotlistReportParameters } from "../module";
import { Row } from "../../layout/flexbox";
import { apiDownloadHotlistReport } from "../../core/redux/api/endpoints";
import { ALL_HOTLIST_ID, ALL_HOTLIST_REASON, TREMBLANT_COVID_ATTESTATION, VERMONT_COVID_ATTESTATION } from "../constants";
import { toDateOnly } from "../../language/time";
import { HTTP_STATUS } from "../../core/redux/api/reduxStandardApiAction";
import { css } from "@emotion/react";

// Styles
const formStyle = css`
  display: flex;
  flex-direction: column;
  ${COLUMN_SPACING};
  max-width: 360px;
  margin-top: 20px;
  min-height: 300px;
`;

const labelContainerStyle = css`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  ${ROW_SPACING_WIDE};
`;

// Helpers
const getTypeKey = (reason: HotlistType) => reason.id;
const getTypeValue = (reason: HotlistType) => reason.id;
const getTypeLabel = (reason: HotlistType) => reason.description;
const formatDatePlaceholder = (dateFormat?: string) => dateFormat ? dateFormat.replace(/[A-Za-z]/g, "_") : "";

// Form State and reducer
interface FormState {
  hotlistType: HotlistType | null;
  fromDate: Date | null;
  toDate: Date | null;
  errorMsg: string | null;
}
 
type FormAction = { type: "SET_FIELD"; field: keyof FormState; value: any };

const formReducer = (state: FormState, action: FormAction): FormState => {
  switch (action.type) {
    case "SET_FIELD":
      return { ...state, [action.field]: action.value };
    default:
      return state;
  }
};

const HotlistReportParameters: React.FC = () => {
  const dispatch = useDispatch();
  const authToken = useSelector((state: IAppState) => state.auth.bearerToken);
  const resources = useSelector((state: IAppState) => state.locale);
  const selectedResort = useSelector((state: IAppState) => state.selectedResort);
  const allHotlistTypes: HotlistType[] | undefined = useSelector((state: IAppState) => state.entitlementHotlistTypes?.types);
  const loadingHotlistTypes: boolean | undefined = useSelector((state: IAppState) => state.entitlementHotlistTypes?.isLoading);
  const errorHotlistTypes: string | undefined = useSelector((state: IAppState) => state.entitlementHotlistTypes?.error);
  const dateFormat = useSelector((state: IAppState) => state.defaultDateFormat);

  const [hotlistTypes, setHotlistTypes] = React.useState<HotlistType[]>([]);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [enableSubmit, setEnableSubmit] = React.useState<boolean>(true);

  const [formState, dispatchForm] = React.useReducer(formReducer, {
    hotlistType: null,
    fromDate: null,
    toDate: null,
    errorMsg: null,
  });

  const validateForm = (): string | null => {
    return validateHotlistReportParameters(resources, formState.hotlistType, formState.fromDate, formState.toDate);
  };

  const handleSubmit = async (event: React.FormEvent) => {
      event.preventDefault();
      const error = validateForm();
      if (error) {
        dispatchForm({ type: "SET_FIELD", field: "errorMsg", value: error });
        return;
      }
      dispatchForm({type: "SET_FIELD", field: "errorMsg", value: undefined});
      setIsSubmitting(true);
      setEnableSubmit(false);
      await downloadHotlistReport();
      setIsSubmitting(false);
    };

  const downloadHotlistReport = async () => {
    try {
      const fileName = `${selectedResort.resortAbbreviation}_HotlistReport.csv`;
      const hotlistTypeId = formState.hotlistType?.id === ALL_HOTLIST_ID ? null : formState.hotlistType?.id ?? null;
      const apiUrl = apiDownloadHotlistReport(
        selectedResort.resortAbbreviation,
        encodeURIComponent(formState.fromDate!.toISOString()),
        encodeURIComponent(formState.toDate!.toISOString()),
        hotlistTypeId
      );

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "content-type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      });

      switch(response.status)
      {
        case HTTP_STATUS.OK: {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          break;
        }
        
        case HTTP_STATUS.NO_CONTENT: {
          // No content
          const message = getResource(resources, "REPORTS_HOTLIST_ERROR_NO_DATA");
          dispatchForm({ type: "SET_FIELD", field: "errorMsg", value: message });
          break;
        }

        case HTTP_STATUS.BAD_REQUEST: {
          // Validation error
          const details = await response.json();
          const message = `${getResource(resources, "REPORTS_HOTLIST_INVALID_PARAMETERS")} ${details?.detail}`;
          dispatchForm({ type: "SET_FIELD", field: "errorMsg", value: message });
          break;
        }
        
        case HTTP_STATUS.CLIENT_CLOSED_REQUEST: {
          // Client canceled request
          const message = getResource(resources, "REPORTS_HOTLIST_REQUEST_CANCELLED");
          dispatchForm({ type: "SET_FIELD", field: "errorMsg", value: message });
          break;
        }

       default: {
          // Unexpected error
          const message = getResource(resources, "REPORTS_HOTLIST_ERROR_DOWNLOAD_FAILED");
          dispatchForm({ type: "SET_FIELD", field: "errorMsg", value: message });
          break;
        }
      }
    } catch (error) {
      const message = getResource(resources, "REPORTS_HOTLIST_ERROR_GENERATING_CSV_FILE");
      dispatchForm({ type: "SET_FIELD", field: "errorMsg", value: message });
    }
  };

  React.useEffect(() => {
    dispatch(getHotlistTypesAction());
  }, [dispatch]);

  React.useEffect(() => {
    if (allHotlistTypes) {
      const filtered = allHotlistTypes.filter(ht => ht.id !== VERMONT_COVID_ATTESTATION && ht.id !== TREMBLANT_COVID_ATTESTATION);
      setHotlistTypes([ALL_HOTLIST_REASON, ...filtered]);
    } else {
      setHotlistTypes([]);
    }
  }, [allHotlistTypes]);

  React.useEffect(() => {
      setEnableSubmit(true);
  }, [formState.fromDate, formState.toDate, formState.hotlistType])

  React.useEffect(() => {
    if (formState.fromDate != null && formState.toDate != null) {
      var error = validateHotlistReportDates(resources, formState.fromDate, formState.toDate)
      if (error) {
        dispatchForm({ type: "SET_FIELD", field: "errorMsg", value: error });
        return;
      }
      dispatchForm({type: "SET_FIELD", field: "errorMsg", value: undefined});
    }
  }, [formState.fromDate, formState.toDate, resources])

  return (
    <form css={formStyle} onSubmit={handleSubmit}>
      {/*Reason Drop-down*/}
      <LabelGroup
        label={getResource(resources, "REPORTS_HOTLIST_REASON")}
        showRequired
      >
        <Dropdown
          resources={resources}
          items={hotlistTypes || []}
          selectedItem={formState.hotlistType}
          placeholderMessage={getResource(
            resources,
            "REPORTS_HOTLIST_REASON_PLACEHOLDER"
          )}
          disabled={
            loadingHotlistTypes ||
            !!errorHotlistTypes ||
            isNilOrEmpty(hotlistTypes) ||
            isSubmitting
          }
          getKey={getTypeKey}
          getValue={getTypeValue}
          getLabel={getTypeLabel}
          noItemsMessage={getResource(
            resources,
            "REPORTS_HOTLIST_REASON_FAILED"
          )}
          onChange={(reason) => dispatchForm({type: "SET_FIELD", field: "hotlistType", value: reason })}
          autoFocus
          rootCss={css`width: 300px;`}
        />
      </LabelGroup>
      <LabelGroup label={getResource(resources, 'REPORTS_HOTLIST_CREATED_DATE')}
        showRequired>
        <div css={labelContainerStyle}>
          {/* Effective (/start) date selector */}
          <LabelGroup
            label={getResource(resources, "REPORTS_HOTLIST_FROM_LABEL")}
            rootCss={css`
              width: auto;
            `}
            subLabel={`(${dateFormat?.toUpperCase()})`}
            subLabelCss={css`
              font-weight: normal;
              ${TinyText}
            `}
            showRequired={true}
          >
            <DateInput
              date={formState.fromDate || undefined}
              disabled={isSubmitting}
              onChange={(date) => dispatchForm({ type: "SET_FIELD", field: "fromDate", value: date })}
              placeholderText={formatDatePlaceholder(dateFormat)}
              tooltipText={getResource(
                resources,
                "REPORTS_HOTLIST_FROM_DATE_TOOLTIP"
              )}
              dateFormat={dateFormat}
              maxDate={toDateOnly(new Date())}
              rootCss={css`width: 9.4rem;`}
            />
          </LabelGroup>
          {/* Expiration (/end) date selector */}
          <LabelGroup
            label={getResource(resources, "REPORTS_HOTLIST_TO_LABEL")}
            rootCss={css`
              width: auto;
            `}
            subLabel={`(${dateFormat?.toUpperCase()})`}
            subLabelCss={css`
              font-weight: normal;
              ${TinyText}
            `}
            showRequired={true}
          >
            <DateInput
              date={formState.toDate || undefined}
              disabled={isSubmitting}
              onChange={(date) => dispatchForm({ type: "SET_FIELD", field: "toDate", value: date })}
              placeholderText={formatDatePlaceholder(dateFormat)}
              tooltipText={getResource(
                resources,
                "REPORTS_HOTLIST_TO_DATE_TOOLTIP"
              )}
              dateFormat={dateFormat}
              maxDate={toDateOnly(new Date())}
              rootCss={css`width: 9.4rem;`}
            />
          </LabelGroup>
        </div>
      
      </LabelGroup>

      {/* Validation Message */}
      <ErrorView error={formState.errorMsg || undefined} />

      <Row
        justifyContent="flex-end"
        rootCss={css`
          margin-top: 35px;
          ${ROW_SPACING}
        `}
      >
        <Buttons.BigNeutral
          type="submit"
          disabled={!enableSubmit}
          title={getResource(resources, "REPORTS_EXPORT_HOTLIST_REPORT")}
        >
          {getResource(resources, "REPORTS_EXPORT_REPORT")}
        </Buttons.BigNeutral>
      </Row>
    </form>
  );
};

export default HotlistReportParameters;
